import { PlatformSla } from '@provision/entity-types';
import { gql, useQuery } from '@provision/graphql-client';
import { platformSlaSortIndex } from '@provision/membership-plans-ui';
import { ServiceLevel } from '@provision/service-level-utils';
import { useMemo } from 'react';
import { isNullish } from 'remeda';
import {
  PartnerGroupRules,
  UserGroupRules,
} from 'src/graphql/entities/group-rules/group-rules.types';
import { usePartnerGroupRules } from 'src/graphql/entities/group-rules/partner-group-rules';
import { useUserGroupRuleValues } from 'src/graphql/entities/group-rules/user-group-rules';
import { filterSecurityOnlyOption } from 'src/graphql/entities/security-only';
import { QueryVariables, Records } from 'src/graphql/types';
import { ServiceLevelOption } from './types';

const GET_PLATFORM_SLA_QUERY = gql`
  query GetPlatformSla($params: QueryParams) @entity(name: "PlatformSla") {
    getPlatformSla(queryParams: $params) {
      records {
        id
        platformSlaId
        displayName
        isActive
      }
    }
  }
`;

interface GetPlatformSlaResponse {
  getPlatformSla: Records<
    Pick<PlatformSla, 'id' | 'platformSlaId' | 'displayName' | 'isActive'>
  >;
}

export function useServiceLevels(
  partnerId?: number | null,
  { allowWhenNoPartner = false }: { allowWhenNoPartner?: boolean } = {},
) {
  const { data, loading } = useQuery<GetPlatformSlaResponse, QueryVariables>(
    GET_PLATFORM_SLA_QUERY,
    {
      variables: {
        params: {
          filters: [
            {
              attribute: 'isActive',
              operator: '=',
              value: 'true',
            },
          ],
        },
      },
    },
  );

  const { userGroupRules } = useUserGroupRuleValues();
  const { partnerGroupRules } = usePartnerGroupRules(partnerId);

  // platform SLA tiers are partner-agnostic. When no partner is selected (platform/admin rule
  // editing), surfaces that opt in via `allowWhenNoPartner` show the catalog ungated instead of empty
  const hasServiceLevelAccess =
    (allowWhenNoPartner && isNullish(partnerId)) ||
    Boolean(partnerGroupRules[PartnerGroupRules.AccessToPlatformSLAs]);

  const canUseSecurityOnlySla =
    Boolean(userGroupRules[UserGroupRules.AccessToSetSecurityOnlySla]) &&
    Boolean(partnerGroupRules[PartnerGroupRules.AccessToSecurityOnlySla]);

  return useMemo(() => {
    if (!hasServiceLevelAccess) {
      return {
        serviceLevelOptions: [] as ServiceLevelOption[],
        serviceLevelFilterOptions: [] as ServiceLevelOption[],
        loading,
        hasServiceLevelAccess,
      };
    }

    const slaOptions: ServiceLevelOption[] = (data?.getPlatformSla.records ?? [])
      .map((record) => ({
        id: record.displayName,
        value: record.displayName,
        label: record.displayName,
        platformSlaId: record.platformSlaId,
      }))
      .sort(
        (a, b) =>
          platformSlaSortIndex(a.platformSlaId) - platformSlaSortIndex(b.platformSlaId),
      );

    const nonMemberOption: ServiceLevelOption = {
      id: ServiceLevel.NonMember,
      value: ServiceLevel.NonMember,
      label: ServiceLevel.NonMember,
      platformSlaId: null,
    };

    const serviceLevelFilterOptions = [nonMemberOption, ...slaOptions];

    const serviceLevelOptions = filterSecurityOnlyOption(
      serviceLevelFilterOptions,
      canUseSecurityOnlySla,
    );

    return {
      serviceLevelOptions,
      serviceLevelFilterOptions,
      loading,
      hasServiceLevelAccess,
    };
  }, [data, loading, canUseSecurityOnlySla, hasServiceLevelAccess]);
}
